import { ChangeDetectionStrategy, Component, inject, InjectionToken, OnInit, signal } from '@angular/core';

/** The page's location (a token, so specs can set the host and path). */
export const PREVIEW_BANNER_LOCATION = new InjectionToken<Pick<Location, 'hostname' | 'href'>>(
  'PREVIEW_BANNER_LOCATION',
  { providedIn: 'root', factory: () => location },
);

/**
 * What `/build-info.json` says about a preview. `path`: this browser is on a path preview (written by
 * scripts/path-preview.sh); `live`: live dev itself is serving a branch build (a `dev-preview-*` tag, or
 * `deploy-to-dev` on a branch other than `main`), which nobody can leave from the browser.
 */
export interface PathPreviewInfo {
  kind: 'path' | 'live';
  name: string;
  build: string | null;
  commit: string | null;
}

/** Path previews exist only on dev: the edge routes `?preview=<name>` on `*.aiworkbook.dev` hosts only. */
export function previewHost(hostname: string): boolean {
  return hostname.endsWith('.aiworkbook.dev');
}

/** The same page on live: the edge clears the preview cookie for `?preview=off` and redirects without it. */
export function returnToLiveUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.set('preview', 'off');
  return url.pathname + url.search + url.hash;
}

/** Reads a `/build-info.json` body: a path preview, live dev on a branch build, or nothing (main builds, no marker). */
export function previewFromBuildInfo(info: Record<string, unknown> | null): PathPreviewInfo | null {
  if (!info || typeof info !== 'object') return null;
  const text = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? String(v) : null);
  const build = text(info['build_number']);
  const commit = text(info['commit'])?.slice(0, 7) ?? null;
  if (info['source'] === 'path-preview' && typeof info['name'] === 'string') {
    return { kind: 'path', name: info['name'], build, commit };
  }
  const ref = text(info['ref']);
  const liveSource = info['source'] === 'preview-tag' || info['source'] === 'custom';
  if (liveSource && ref && ref !== 'main') return { kind: 'live', name: ref, build, commit };
  return null;
}

/**
 * A thin bar when this browser is on a preview. On a path preview (`source: path-preview`, served by the edge from the
 * preview's own copy) it offers Return to live. When live dev itself serves a branch build (`preview-tag`, or `custom`
 * from a branch other than `main`) it only says so: there is no live to return to; restoring `main` is a pipeline run.
 * Fixed and click-through except its link: it never shifts the layout or covers the page's controls. `main` builds and
 * a `deploy-to-dev` restore of `main`: no bar.
 */
@Component({
  selector: 'app-preview-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (preview(); as p) {
      <div class="preview-banner" role="status">
        <span
          >{{ p.kind === 'path' ? 'Preview mode' : 'Dev is running a preview' }} · {{ p.name }}
          @if (p.build) {
            · build {{ p.build }}
          }
          @if (p.commit) {
            · {{ p.commit }}
          }
        </span>
        @if (p.kind === 'path') {
          <a [href]="returnUrl">Return to live</a>
        }
      </div>
    }
  `,
  styles: `
    .preview-banner {
      position: fixed;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      z-index: 2147483000;
      display: flex;
      gap: 12px;
      align-items: center;
      padding: 2px 12px;
      border-radius: 0 0 6px 6px;
      background: rgba(17, 24, 39, 0.72);
      color: #fff;
      font:
        12px/18px system-ui,
        sans-serif;
      pointer-events: none;
    }
    .preview-banner a {
      color: #fde68a;
      text-decoration: underline;
      pointer-events: auto;
    }
  `,
})
export class PreviewBannerComponent implements OnInit {
  private readonly where = inject(PREVIEW_BANNER_LOCATION);
  readonly preview = signal<PathPreviewInfo | null>(null);
  readonly returnUrl = returnToLiveUrl(this.where.href);

  ngOnInit(): void {
    if (previewHost(this.where.hostname)) void this.check();
  }

  private async check(): Promise<void> {
    try {
      const response = await fetch('/build-info.json', { cache: 'no-store', credentials: 'same-origin' });
      if (!response.ok) return;
      this.preview.set(previewFromBuildInfo((await response.json()) as Record<string, unknown> | null));
    } catch {
      // no marker (local, prod): no bar
    }
  }
}
